var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { EntityFile, } from '@verdant-web/store';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore, } from 'react';
import { suspend } from 'suspend-react';
import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/with-selector.js';
function isQueryCurrentValid(query) {
    return !(query.status === 'initial' || query.status === 'initializing');
}
function useLiveQuery(liveQuery, disableSuspense = false) {
    // suspend if the query doesn't have a valid result set yet.
    if (!disableSuspense && liveQuery && !isQueryCurrentValid(liveQuery)) {
        suspend(() => liveQuery.resolved, [liveQuery]);
    }
    return useSyncExternalStore((callback) => {
        if (liveQuery) {
            return liveQuery.subscribe('change', callback);
        }
        else {
            return () => { };
        }
    }, () => {
        return liveQuery ? liveQuery.current : null;
    });
}
function useLiveQueryStatus(liveQuery) {
    return useSyncExternalStore((callback) => {
        if (liveQuery) {
            return liveQuery.subscribe('statusChange', callback);
        }
        else {
            return () => { };
        }
    }, () => {
        var _a;
        return (_a = liveQuery === null || liveQuery === void 0 ? void 0 : liveQuery.status) !== null && _a !== void 0 ? _a : 'initial';
    });
}
function capitalize(str) {
    return (str.charAt(0).toUpperCase() + str.slice(1));
}
export function createHooks(schema, options = {}) {
    var _a;
    const Context = (_a = options.Context) !== null && _a !== void 0 ? _a : createContext(null);
    function useStorage() {
        const ctx = useContext(Context);
        if (!ctx) {
            throw new Error('No verdant provider was found');
        }
        return suspend(() => ctx.readyPromise, ['lofi_' + ctx.namespace]);
    }
    function useWatch(liveObject, options) {
        return useSyncExternalStore((handler) => {
            if (liveObject) {
                if ('isFile' in liveObject) {
                    return liveObject.subscribe('change', handler);
                }
                else {
                    if (options === null || options === void 0 ? void 0 : options.deep) {
                        return liveObject.subscribe('change', handler);
                    }
                    else {
                        return liveObject.subscribe('change', handler);
                    }
                }
            }
            return () => { };
        }, () => {
            if (liveObject) {
                if (liveObject instanceof EntityFile) {
                    return liveObject.url;
                }
                else {
                    return liveObject.getAll();
                }
            }
            return undefined;
        });
    }
    function useOnChange(liveObject, handler, options) {
        const handlerRef = useRef(handler);
        handlerRef.current = handler;
        return useEffect(() => {
            if (!liveObject)
                return;
            if ('isFile' in liveObject) {
                return liveObject === null || liveObject === void 0 ? void 0 : liveObject.subscribe('change', () => {
                    handlerRef.current({});
                });
            }
            else {
                if (options === null || options === void 0 ? void 0 : options.deep) {
                    return liveObject === null || liveObject === void 0 ? void 0 : liveObject.subscribe('changeDeep', (target, info) => {
                        handlerRef.current(Object.assign(Object.assign({}, info), { target: target }));
                    });
                }
                return liveObject === null || liveObject === void 0 ? void 0 : liveObject.subscribe('change', (info) => {
                    var _a;
                    (_a = info.isLocal) !== null && _a !== void 0 ? _a : (info.isLocal = false);
                    handlerRef.current(info);
                });
            }
        }, [liveObject, handlerRef]);
    }
    function useSelf() {
        const storage = useStorage();
        return useSyncExternalStore((callback) => storage.sync.presence.subscribe('selfChanged', callback), () => storage.sync.presence.self);
    }
    function usePeerIds() {
        const storage = useStorage();
        return useSyncExternalStore((callback) => storage.sync.presence.subscribe('peersChanged', callback), () => storage.sync.presence.peerIds);
    }
    function usePeer(peerId) {
        const storage = useStorage();
        return useSyncExternalStore((callback) => {
            const unsubs = [];
            unsubs.push(storage.sync.presence.subscribe('peerChanged', (id, user) => {
                if (id === peerId) {
                    callback();
                }
            }));
            unsubs.push(storage.sync.presence.subscribe('peerLeft', (id) => {
                if (id === peerId) {
                    callback();
                }
            }));
            return () => {
                unsubs.forEach((unsub) => unsub());
            };
        }, () => { var _a; return (peerId ? (_a = storage.sync.presence.peers[peerId]) !== null && _a !== void 0 ? _a : null : null); });
    }
    function useFindPeer(query, options) {
        const storage = useStorage();
        return useSyncExternalStore((callback) => {
            const unsubs = [];
            unsubs.push(storage.sync.presence.subscribe('peerChanged', (id, user) => {
                if (query(user)) {
                    callback();
                }
            }));
            unsubs.push(storage.sync.presence.subscribe('peerLeft', (id) => {
                if (query(storage.sync.presence.peers[id])) {
                    callback();
                }
            }));
            if (options === null || options === void 0 ? void 0 : options.includeSelf) {
                unsubs.push(storage.sync.presence.subscribe('selfChanged', (user) => {
                    if (query(user)) {
                        callback();
                    }
                }));
            }
            return () => {
                unsubs.forEach((unsub) => unsub());
            };
        }, () => {
            const peers = Object.values(storage.sync.presence.peers);
            if (options === null || options === void 0 ? void 0 : options.includeSelf) {
                peers.push(storage.sync.presence.self);
            }
            return peers.find(query) || null;
        });
    }
    function useFindPeers(query, options) {
        const storage = useStorage();
        return useSyncExternalStoreWithSelector((callback) => {
            const unsubs = [];
            unsubs.push(storage.sync.presence.subscribe('peerChanged', (id, user) => {
                if (user && query(user)) {
                    callback();
                }
            }));
            unsubs.push(storage.sync.presence.subscribe('peerLeft', (id) => {
                callback();
            }));
            if (options === null || options === void 0 ? void 0 : options.includeSelf) {
                unsubs.push(storage.sync.presence.subscribe('selfChanged', (user) => {
                    if (query(user)) {
                        callback();
                    }
                }));
            }
            return () => {
                unsubs.forEach((unsub) => unsub());
            };
        }, () => {
            const peers = Object.values(storage.sync.presence.peers).filter(Boolean);
            if (options === null || options === void 0 ? void 0 : options.includeSelf) {
                peers.push(storage.sync.presence.self);
            }
            return peers.filter((p) => !!p).filter(query);
        }, () => [], (peers) => peers, (a, b) => a.length === b.length && a.every((peer, i) => peer === b[i]));
    }
    function useSyncStatus() {
        const storage = useStorage();
        return useSyncExternalStore((callback) => storage.sync.subscribe('onlineChange', callback), () => storage.sync.isConnected);
    }
    function useUndo() {
        const storage = useStorage();
        return useCallback(() => storage.undoHistory.undo(), [storage]);
    }
    function useRedo() {
        const storage = useStorage();
        return useCallback(() => storage.undoHistory.redo(), [storage]);
    }
    function useCanUndo() {
        const storage = useStorage();
        return useSyncExternalStore((callback) => storage.undoHistory.subscribe('change', callback), () => storage.undoHistory.canUndo);
    }
    function useCanRedo() {
        const storage = useStorage();
        return useSyncExternalStore((callback) => storage.undoHistory.subscribe('change', callback), () => storage.undoHistory.canRedo);
    }
    function useUnsuspendedClient() {
        const desc = useContext(Context);
        const client = desc === null || desc === void 0 ? void 0 : desc.current;
        const [_, forceUpdate] = useState(0);
        useEffect(() => {
            if (desc && !client) {
                desc.readyPromise.then(() => forceUpdate((n) => n + 1));
            }
        }, [desc, client]);
        return client || null;
    }
    /**
     * Non-suspending hook which allows declarative sync start/stop
     * control.
     *
     * You can optionally configure parameters as part of this as well.
     */
    function useSync(isOn, config = {}) {
        const client = useUnsuspendedClient();
        useEffect(() => {
            if (client) {
                if (isOn) {
                    client.sync.start();
                }
                else {
                    client.sync.stop();
                }
            }
        }, [client, isOn]);
        useEffect(() => {
            if (client) {
                if (config.mode !== undefined) {
                    client.sync.setMode(config.mode);
                }
            }
        }, [client, config.mode]);
        useEffect(() => {
            if (client) {
                if (config.pullInterval !== undefined) {
                    client.sync.setPullInterval(config.pullInterval);
                }
            }
        }, [client, config.pullInterval]);
    }
    function SyncController({ isOn }) {
        useSync(isOn);
        return null;
    }
    const hooks = {
        useStorage,
        useClient: useStorage,
        useUnsuspendedClient,
        useWatch,
        useOnChange,
        useSelf,
        usePeerIds,
        usePeer,
        useFindPeer,
        useFindPeers,
        useSyncStatus,
        useUndo,
        useRedo,
        useCanUndo,
        useCanRedo,
        useSync,
        Context,
        Provider: (_a) => {
            var { value, children, sync } = _a, rest = __rest(_a, ["value", "children", "sync"]);
            // auto-open storage when used in provider
            useMemo(() => {
                value.open();
            }, [value]);
            return (_jsxs(Context.Provider, Object.assign({ value: value }, rest, { children: [children, sync !== undefined && _jsx(SyncController, { isOn: sync })] })));
        },
    };
    const collectionNames = Object.keys(schema.collections);
    for (const pluralName of collectionNames) {
        const collection = schema.collections[pluralName];
        const getOneHookName = `use${capitalize(collection.name)}`;
        hooks[getOneHookName] = function useIndividual(id, { skip } = {}) {
            const storage = useStorage();
            const liveQuery = useMemo(() => {
                return skip ? null : storage[pluralName].get(id);
            }, [id, skip]);
            const data = useLiveQuery(liveQuery);
            return data;
        };
        hooks[getOneHookName + 'Unsuspended'] = function useIndividualUnsuspended(id, { skip } = {}) {
            const storage = useStorage();
            const liveQuery = useMemo(() => {
                return skip ? null : storage[pluralName].get(id);
            }, [id, skip]);
            const data = useLiveQuery(liveQuery, true);
            const status = useLiveQueryStatus(liveQuery);
            return { data, status };
        };
        const findOneHookName = `useOne${capitalize(collection.name)}`;
        hooks[findOneHookName] = function useOne({ skip, index, key, } = {}) {
            const storage = useStorage();
            const liveQuery = useMemo(() => {
                return skip ? null : storage[pluralName].findOne({ index, key });
            }, [index, skip]);
            const data = useLiveQuery(liveQuery);
            return data;
        };
        hooks[findOneHookName + 'Unsuspended'] = function useOneUnsuspended({ skip, index, key, } = {}) {
            const storage = useStorage();
            const liveQuery = useMemo(() => {
                return skip ? null : storage[pluralName].findOne({ index, key });
            }, [index, skip]);
            const data = useLiveQuery(liveQuery, true);
            const status = useLiveQueryStatus(liveQuery);
            return { data, status };
        };
        const getAllHookName = `useAll${capitalize(pluralName)}`;
        hooks[getAllHookName] = function useAll({ index, skip, key, } = {}) {
            const storage = useStorage();
            // assumptions: this query getter is fast and returns the same
            // query identity for subsequent calls.
            const liveQuery = useMemo(() => (skip ? null : storage[pluralName].findAll({ index, key })), [index, skip]);
            const data = useLiveQuery(liveQuery);
            return data || [];
        };
        hooks[getAllHookName + 'Unsuspended'] = function useAllUnsuspended({ index, skip, key, } = {}) {
            const storage = useStorage();
            // assumptions: this query getter is fast and returns the same
            // query identity for subsequent calls.
            const liveQuery = useMemo(() => (skip ? null : storage[pluralName].findAll({ index, key })), [index, skip]);
            const data = useLiveQuery(liveQuery, true) || [];
            const status = useLiveQueryStatus(liveQuery);
            return { data, status };
        };
        const getAllPaginatedHookName = `useAll${capitalize(pluralName)}Paginated`;
        hooks[getAllPaginatedHookName] = function useAllPaginated({ index, skip, pageSize = 10, key, suspend, } = {}) {
            const storage = useStorage();
            // assumptions: this query getter is fast and returns the same
            // query identity for subsequent calls.
            const liveQuery = useMemo(() => skip
                ? null
                : storage[pluralName].findPage({
                    index,
                    pageSize,
                    page: 0,
                    key: key || getAllPaginatedHookName,
                }), [index, skip, pageSize]);
            const data = useLiveQuery(liveQuery, suspend === false);
            const status = useLiveQueryStatus(liveQuery);
            const tools = useMemo(() => ({
                next: () => liveQuery === null || liveQuery === void 0 ? void 0 : liveQuery.nextPage(),
                previous: () => liveQuery === null || liveQuery === void 0 ? void 0 : liveQuery.previousPage(),
                setPage: (page) => liveQuery === null || liveQuery === void 0 ? void 0 : liveQuery.setPage(page),
                get hasPrevious() {
                    return liveQuery === null || liveQuery === void 0 ? void 0 : liveQuery.hasPreviousPage;
                },
                get hasNext() {
                    return liveQuery === null || liveQuery === void 0 ? void 0 : liveQuery.hasNextPage;
                },
                status,
            }), [liveQuery, status]);
            return [data, tools];
        };
        const getAllInfiniteHookName = `useAll${capitalize(pluralName)}Infinite`;
        hooks[getAllInfiniteHookName] = function useAllInfinite({ index, skip, pageSize = 10, key, suspend, } = {}) {
            const storage = useStorage();
            // assumptions: this query getter is fast and returns the same
            // query identity for subsequent calls.
            const liveQuery = useMemo(() => skip
                ? null
                : storage[pluralName].findAllInfinite({
                    index,
                    pageSize,
                    key: key || getAllInfiniteHookName,
                }), [index, skip, pageSize]);
            const data = useLiveQuery(liveQuery, suspend === false);
            const status = useLiveQueryStatus(liveQuery);
            const tools = useMemo(() => ({
                loadMore: () => liveQuery === null || liveQuery === void 0 ? void 0 : liveQuery.loadMore(),
                get hasMore() {
                    return liveQuery === null || liveQuery === void 0 ? void 0 : liveQuery.hasMore;
                },
                status,
            }), [liveQuery, status]);
            return [data, tools];
        };
    }
    hooks.withMutations = (mutations) => {
        const augmentedHooks = Object.assign({}, hooks);
        for (const [name, subHook] of Object.entries(mutations)) {
            augmentedHooks[name] = (...args) => {
                const client = hooks.useClient();
                return subHook(client, ...args);
            };
        }
        return augmentedHooks;
    };
    return hooks;
}
//# sourceMappingURL=hooks.js.map